import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mailer-settings" }
const _hoisted_2 = { class: "d-flex gap-3 mt-4" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { key: 3 }
const _hoisted_8 = { key: 4 }
const _hoisted_9 = { class: "d-flex gap-3 mt-4" }

import { ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'

// SMTP Configuration State

export default /*@__PURE__*/_defineComponent({
  __name: 'MailerSettings',
  setup(__props) {

const smtpConfig = ref({
  smtp_host: 'smtp.gmail.com',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_from_email: 'noreply@spacecoaststem.org',
  smtp_from_name: 'Space Coast STEM',
  smtp_use_tls: true,
  portal_url: 'https://portal.spacecoaststem.org',
  organization_address: ''
})

const originalSmtpConfig = ref({})
const showPassword = ref(false)
const savingConfig = ref(false)
const loadingConfig = ref(false)
const configSuccessMessage = ref('')
const configErrorMessage = ref('')

// Email Testing State
const testEmail = ref('')
const sendingTest = ref(false)
const selectedTemplate = ref('')
const sendingTemplate = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const templates = [
  { title: 'Teacher Invite', value: 'teacher_invite' },
  { title: 'Student Request (Existing Teacher)', value: 'student_request_existing' },
  { title: 'Student Request (New Teacher)', value: 'student_request_new' },
  { title: 'New Inventory Available', value: 'new_inventory' },
  { title: 'Request Filled Notification', value: 'request_filled' },
  { title: 'New User Admin Notification', value: 'new_user_admin_notification' },
  { title: 'User Account Approved', value: 'user_account_approved' }
]

const templateData = ref<any>({
  // Common fields
  teacher_email: '',
  teacher_name: 'Jane Smith',
  student_name: 'John Doe',
  equipment_type: 'Laptop',
  school_name: 'Sample High School',
  invited_by: 'Admin User',

  // New inventory
  recipient_email: '',
  quantity: 5,

  // Request filled
  requester_email: '',
  requester_name: 'Parent Name',
  equipment_details: 'Dell Latitude 5490, Intel Core i5, 8GB RAM, 256GB SSD',
  asset_tag: 'SCT-2024-001',
  pickup_instructions: 'Equipment can be picked up at the school office during regular hours (8 AM - 3 PM, Monday-Friday). Please bring this email and a valid ID.',
  contact_info: 'If you have questions, please contact us at info@spacecoaststem.org or call (321) 555-0100.'
})

const hasChanges = computed(() => {
  return JSON.stringify(smtpConfig.value) !== JSON.stringify(originalSmtpConfig.value)
})

const canSendTemplate = computed(() => {
  if (!selectedTemplate.value) return false

  switch (selectedTemplate.value) {
    case 'teacher_invite':
      return !!templateData.value.teacher_email && !!templateData.value.teacher_name
    case 'student_request_existing':
    case 'student_request_new':
      return !!templateData.value.teacher_email &&
             !!templateData.value.teacher_name &&
             !!templateData.value.student_name &&
             !!templateData.value.equipment_type
    case 'new_inventory':
      return !!templateData.value.recipient_email &&
             !!templateData.value.equipment_type &&
             templateData.value.quantity > 0
    case 'request_filled':
      return !!templateData.value.requester_email &&
             !!templateData.value.requester_name &&
             !!templateData.value.student_name
    default:
      return false
  }
})

// Load SMTP configuration
const loadSmtpConfig = async () => {
  loadingConfig.value = true
  configErrorMessage.value = ''
  configSuccessMessage.value = ''

  try {
    const response = await api.get('/configuration')
    const config = response.data

    smtpConfig.value = {
      smtp_host: config.smtp_host || 'smtp.gmail.com',
      smtp_port: config.smtp_port || 587,
      smtp_user: config.smtp_user || '',
      smtp_password: config.smtp_password || '',
      smtp_from_email: config.smtp_from_email || 'noreply@spacecoaststem.org',
      smtp_from_name: config.smtp_from_name || 'Space Coast STEM',
      smtp_use_tls: config.smtp_use_tls !== false,
      portal_url: config.portal_url || 'https://portal.spacecoaststem.org',
      organization_address: config.organization_address || ''
    }

    originalSmtpConfig.value = JSON.parse(JSON.stringify(smtpConfig.value))
  } catch (error: any) {
    console.error('Error loading SMTP config:', error)
    configErrorMessage.value = 'Failed to load SMTP configuration'
  } finally {
    loadingConfig.value = false
  }
}

// Save SMTP configuration
const saveSmtpSettings = async () => {
  savingConfig.value = true
  configErrorMessage.value = ''
  configSuccessMessage.value = ''

  try {
    await api.put('/configuration', smtpConfig.value)

    originalSmtpConfig.value = JSON.parse(JSON.stringify(smtpConfig.value))
    configSuccessMessage.value = 'SMTP configuration saved successfully!'
  } catch (error: any) {
    console.error('Error saving SMTP config:', error)
    configErrorMessage.value = error.response?.data?.detail || 'Failed to save SMTP configuration'
  } finally {
    savingConfig.value = false
  }
}

// Send test email
const sendTestEmail = async () => {
  sendingTest.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await api.post('/admin/debug/test-email', {
      to_email: testEmail.value
    })

    if (response.data?.success) {
      successMessage.value = `Test email sent successfully to ${testEmail.value}!`
    } else {
      errorMessage.value = response.data?.message || 'Failed to send test email'
    }
  } catch (error: any) {
    console.error('Error sending test email:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to send test email. Check SMTP configuration.'
  } finally {
    sendingTest.value = false
  }
}

// Send template test
const sendTemplateTest = async () => {
  sendingTemplate.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await api.post('/admin/debug/test-email-template', {
      template_type: selectedTemplate.value,
      data: templateData.value
    })

    if (response.data?.success) {
      successMessage.value = `Template email sent successfully!`
    } else {
      errorMessage.value = response.data?.message || 'Failed to send template email'
    }
  } catch (error: any) {
    console.error('Error sending template email:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to send template email. Check configuration.'
  } finally {
    sendingTemplate.value = false
  }
}

// Preview template
const previewTemplate = async () => {
  errorMessage.value = ''

  try {
    const response = await api.post('/admin/debug/preview-email-template', {
      template_type: selectedTemplate.value,
      data: templateData.value
    }, {
      headers: {
        'Accept': 'text/html'
      }
    })

    // Open the HTML in a new window
    const previewWindow = window.open('', '_blank')
    if (previewWindow) {
      previewWindow.document.write(response.data)
      previewWindow.document.close()
    } else {
      errorMessage.value = 'Please allow popups to preview emails.'
    }
  } catch (error: any) {
    console.error('Error previewing template:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to preview template. Please try again.'
  }
}

onMounted(() => {
  loadSmtpConfig()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_textarea = _resolveComponent("v-textarea")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_card, { class: "mb-6" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card_title, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, { start: "" }, {
              default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                _createTextVNode("mdi-email-settings", -1)
              ]))]),
              _: 1
            }),
            _cache[41] || (_cache[41] = _createTextVNode(" SMTP Configuration ", -1))
          ]),
          _: 1
        }),
        _createVNode(_component_v_card_text, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_alert, {
              type: "info",
              variant: "tonal",
              class: "mb-6"
            }, {
              default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                _createElementVNode("strong", null, "Email Server Settings", -1),
                _createElementVNode("p", { class: "text-caption mb-0 mt-2" }, [
                  _createTextVNode(" Configure your SMTP server settings to enable email functionality. For Gmail, you need to use an App Password. "),
                  _createElementVNode("a", {
                    href: "https://myaccount.google.com/apppasswords",
                    target: "_blank",
                    class: "text-primary"
                  }, "Get Gmail App Password")
                ], -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_form, {
              ref: "smtpForm",
              onSubmit: _withModifiers(saveSmtpSettings, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "8"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_host,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((smtpConfig.value.smtp_host) = $event)),
                          label: "SMTP Host",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "smtp.gmail.com",
                          hint: "SMTP server address",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_port,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((smtpConfig.value.smtp_port) = $event)),
                          modelModifiers: { number: true },
                          label: "SMTP Port",
                          type: "number",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "587",
                          hint: "Usually 587 for TLS",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_user,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((smtpConfig.value.smtp_user) = $event)),
                          label: "SMTP Username / Email",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "your-email@gmail.com",
                          hint: "Username",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_password,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((smtpConfig.value.smtp_password) = $event)),
                          label: "SMTP Password",
                          type: showPassword.value ? 'text' : 'password',
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "Gmail App Password",
                          hint: "Password (Use 16 character app-password for Gmail)",
                          "persistent-hint": "",
                          disabled: savingConfig.value,
                          "append-inner-icon": showPassword.value ? 'mdi-eye-off' : 'mdi-eye',
                          "onClick:appendInner": _cache[4] || (_cache[4] = ($event: any) => (showPassword.value = !showPassword.value))
                        }, null, 8, ["modelValue", "type", "disabled", "append-inner-icon"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_from_email,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((smtpConfig.value.smtp_from_email) = $event)),
                          label: "From Email Address",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "noreply@spacecoaststem.org",
                          hint: "Email address shown in 'From' field",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.smtp_from_name,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((smtpConfig.value.smtp_from_name) = $event)),
                          label: "From Name",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "Space Coast STEM",
                          hint: "Sender name shown to recipients",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.portal_url,
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((smtpConfig.value.portal_url) = $event)),
                          label: "Portal URL",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "https://portal.spacecoaststem.org",
                          hint: "Base URL for portal links in emails",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: smtpConfig.value.organization_address,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((smtpConfig.value.organization_address) = $event)),
                          label: "Organization Physical Address",
                          variant: "outlined",
                          density: "comfortable",
                          placeholder: "123 Main St, City, ST 12345",
                          hint: "Physical mailing address (required by CAN-SPAM Act for email compliance)",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_checkbox, {
                          modelValue: smtpConfig.value.smtp_use_tls,
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((smtpConfig.value.smtp_use_tls) = $event)),
                          label: "Use TLS/SSL Encryption",
                          hint: "Recommended: Enable for secure email transmission",
                          "persistent-hint": "",
                          disabled: savingConfig.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    type: "submit",
                    loading: savingConfig.value,
                    disabled: !hasChanges.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                          _createTextVNode("mdi-content-save", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[44] || (_cache[44] = _createTextVNode(" Save Configuration ", -1))
                    ]),
                    _: 1
                  }, 8, ["loading", "disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    variant: "outlined",
                    onClick: loadSmtpConfig,
                    disabled: savingConfig.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [...(_cache[45] || (_cache[45] = [
                          _createTextVNode("mdi-refresh", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[46] || (_cache[46] = _createTextVNode(" Reset ", -1))
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ])
              ]),
              _: 1
            }, 512),
            (configSuccessMessage.value)
              ? (_openBlock(), _createBlock(_component_v_alert, {
                  key: 0,
                  type: "success",
                  variant: "tonal",
                  closable: "",
                  "onClick:close": _cache[10] || (_cache[10] = ($event: any) => (configSuccessMessage.value = '')),
                  class: "mt-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(configSuccessMessage.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (configErrorMessage.value)
              ? (_openBlock(), _createBlock(_component_v_alert, {
                  key: 1,
                  type: "error",
                  variant: "tonal",
                  closable: "",
                  "onClick:close": _cache[11] || (_cache[11] = ($event: any) => (configErrorMessage.value = '')),
                  class: "mt-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(configErrorMessage.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_v_card, null, {
      default: _withCtx(() => [
        _createVNode(_component_v_card_title, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, { start: "" }, {
              default: _withCtx(() => [...(_cache[47] || (_cache[47] = [
                _createTextVNode("mdi-email-check", -1)
              ]))]),
              _: 1
            }),
            _cache[48] || (_cache[48] = _createTextVNode(" Email Testing & Preview ", -1))
          ]),
          _: 1
        }),
        _createVNode(_component_v_card_text, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_alert, {
              type: "info",
              variant: "tonal",
              class: "mb-6"
            }, {
              default: _withCtx(() => [...(_cache[49] || (_cache[49] = [
                _createElementVNode("strong", null, "Test Email Functionality", -1),
                _createElementVNode("p", { class: "text-caption mb-0 mt-2" }, " Send test emails to verify your SMTP configuration and preview email templates. ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card, {
              variant: "outlined",
              class: "mb-6"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, { class: "text-h6" }, {
                  default: _withCtx(() => [...(_cache[50] || (_cache[50] = [
                    _createTextVNode("1. Test SMTP Connection", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: testEmail.value,
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((testEmail).value = $event)),
                      label: "Test Email Address",
                      type: "email",
                      variant: "outlined",
                      density: "comfortable",
                      hint: "Send a test email to verify SMTP configuration",
                      "persistent-hint": ""
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      class: "mt-4",
                      onClick: sendTestEmail,
                      loading: sendingTest.value,
                      disabled: !testEmail.value
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[51] || (_cache[51] = [
                            _createTextVNode("mdi-send", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[52] || (_cache[52] = _createTextVNode(" Send Test Email ", -1))
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card, {
              variant: "outlined",
              class: "mb-6"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, { class: "text-h6" }, {
                  default: _withCtx(() => [...(_cache[53] || (_cache[53] = [
                    _createTextVNode("2. Test Email Templates", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_select, {
                      modelValue: selectedTemplate.value,
                      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((selectedTemplate).value = $event)),
                      items: templates,
                      "item-title": "title",
                      "item-value": "value",
                      label: "Select Template",
                      variant: "outlined",
                      density: "comfortable",
                      class: "mb-4"
                    }, null, 8, ["modelValue"]),
                    (selectedTemplate.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createVNode(_component_v_divider, { class: "mb-4" }),
                          (selectedTemplate.value === 'teacher_invite')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_email,
                                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((templateData.value.teacher_email) = $event)),
                                  label: "Teacher Email",
                                  type: "email",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_name,
                                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((templateData.value.teacher_name) = $event)),
                                  label: "Teacher Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.invited_by,
                                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((templateData.value.invited_by) = $event)),
                                  label: "Invited By",
                                  variant: "outlined",
                                  density: "comfortable"
                                }, null, 8, ["modelValue"])
                              ]))
                            : _createCommentVNode("", true),
                          (selectedTemplate.value === 'student_request_existing')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_email,
                                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((templateData.value.teacher_email) = $event)),
                                  label: "Teacher Email",
                                  type: "email",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_name,
                                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((templateData.value.teacher_name) = $event)),
                                  label: "Teacher Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.student_name,
                                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((templateData.value.student_name) = $event)),
                                  label: "Student Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.equipment_type,
                                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((templateData.value.equipment_type) = $event)),
                                  label: "Equipment Type",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.school_name,
                                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((templateData.value.school_name) = $event)),
                                  label: "School Name",
                                  variant: "outlined",
                                  density: "comfortable"
                                }, null, 8, ["modelValue"])
                              ]))
                            : _createCommentVNode("", true),
                          (selectedTemplate.value === 'student_request_new')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_email,
                                  "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((templateData.value.teacher_email) = $event)),
                                  label: "Teacher Email",
                                  type: "email",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.teacher_name,
                                  "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((templateData.value.teacher_name) = $event)),
                                  label: "Teacher Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.student_name,
                                  "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((templateData.value.student_name) = $event)),
                                  label: "Student Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.equipment_type,
                                  "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((templateData.value.equipment_type) = $event)),
                                  label: "Equipment Type",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.school_name,
                                  "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => ((templateData.value.school_name) = $event)),
                                  label: "School Name",
                                  variant: "outlined",
                                  density: "comfortable"
                                }, null, 8, ["modelValue"])
                              ]))
                            : _createCommentVNode("", true),
                          (selectedTemplate.value === 'new_inventory')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.recipient_email,
                                  "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((templateData.value.recipient_email) = $event)),
                                  label: "Recipient Email",
                                  type: "email",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.equipment_type,
                                  "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((templateData.value.equipment_type) = $event)),
                                  label: "Equipment Type",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.quantity,
                                  "onUpdate:modelValue": _cache[29] || (_cache[29] = ($event: any) => ((templateData.value.quantity) = $event)),
                                  modelModifiers: { number: true },
                                  label: "Quantity",
                                  type: "number",
                                  variant: "outlined",
                                  density: "comfortable"
                                }, null, 8, ["modelValue"])
                              ]))
                            : _createCommentVNode("", true),
                          (selectedTemplate.value === 'request_filled')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.requester_email,
                                  "onUpdate:modelValue": _cache[30] || (_cache[30] = ($event: any) => ((templateData.value.requester_email) = $event)),
                                  label: "Requester Email",
                                  type: "email",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.requester_name,
                                  "onUpdate:modelValue": _cache[31] || (_cache[31] = ($event: any) => ((templateData.value.requester_name) = $event)),
                                  label: "Requester Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.student_name,
                                  "onUpdate:modelValue": _cache[32] || (_cache[32] = ($event: any) => ((templateData.value.student_name) = $event)),
                                  label: "Student Name",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.equipment_type,
                                  "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((templateData.value.equipment_type) = $event)),
                                  label: "Equipment Type",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.equipment_details,
                                  "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event: any) => ((templateData.value.equipment_details) = $event)),
                                  label: "Equipment Details",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.asset_tag,
                                  "onUpdate:modelValue": _cache[35] || (_cache[35] = ($event: any) => ((templateData.value.asset_tag) = $event)),
                                  label: "Asset Tag",
                                  variant: "outlined",
                                  density: "comfortable",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_textarea, {
                                  modelValue: templateData.value.pickup_instructions,
                                  "onUpdate:modelValue": _cache[36] || (_cache[36] = ($event: any) => ((templateData.value.pickup_instructions) = $event)),
                                  label: "Pickup Instructions",
                                  variant: "outlined",
                                  rows: "3",
                                  class: "mb-3"
                                }, null, 8, ["modelValue"]),
                                _createVNode(_component_v_text_field, {
                                  modelValue: templateData.value.contact_info,
                                  "onUpdate:modelValue": _cache[37] || (_cache[37] = ($event: any) => ((templateData.value.contact_info) = $event)),
                                  label: "Contact Info",
                                  variant: "outlined",
                                  density: "comfortable"
                                }, null, 8, ["modelValue"])
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_9, [
                            _createVNode(_component_v_btn, {
                              color: "primary",
                              onClick: previewTemplate,
                              disabled: !selectedTemplate.value,
                              variant: "outlined"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_icon, { start: "" }, {
                                  default: _withCtx(() => [...(_cache[54] || (_cache[54] = [
                                    _createTextVNode("mdi-eye", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _cache[55] || (_cache[55] = _createTextVNode(" Preview in New Tab ", -1))
                              ]),
                              _: 1
                            }, 8, ["disabled"]),
                            _createVNode(_component_v_btn, {
                              color: "success",
                              onClick: sendTemplateTest,
                              loading: sendingTemplate.value,
                              disabled: !canSendTemplate.value
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_icon, { start: "" }, {
                                  default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                                    _createTextVNode("mdi-email-send", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _cache[57] || (_cache[57] = _createTextVNode(" Send Template Test ", -1))
                              ]),
                              _: 1
                            }, 8, ["loading", "disabled"])
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            (successMessage.value)
              ? (_openBlock(), _createBlock(_component_v_alert, {
                  key: 0,
                  type: "success",
                  variant: "tonal",
                  closable: "",
                  "onClick:close": _cache[38] || (_cache[38] = ($event: any) => (successMessage.value = '')),
                  class: "mb-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(successMessage.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (errorMessage.value)
              ? (_openBlock(), _createBlock(_component_v_alert, {
                  key: 1,
                  type: "error",
                  variant: "tonal",
                  closable: "",
                  "onClick:close": _cache[39] || (_cache[39] = ($event: any) => (errorMessage.value = ''))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(errorMessage.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})